import { createBrowserRouter, Navigate, type RouteObject } from 'react-router-dom';
import { AuthGate } from '@/auth/AuthGate';
import { VirtualAccountPage } from '@/features/virtualAccount/VirtualAccountPage';
import { VirtualAccountsPage } from '@/features/virtualAccounts/VirtualAccountsPage';
import { ConsentHelpPage } from '@/features/automation/ConsentHelpPage';
import { OwnBooksPage } from '@/features/onboarding/OwnBooksPage';
import { SharedLanding } from '@/features/sharing/SharedLanding';
import { ReferralLanding } from '@/features/referral/ReferralLanding';
import { SettingsPage } from '@/features/settings/SettingsPage';
import { AccountPage } from '@/features/accounts/AccountPage';
import { RegisterSearchPage } from '@/features/accounts/RegisterSearchPage';
import { AccountsPage } from '@/features/accounts/AccountsPage';
import { HoldingPage } from '@/features/holdings/HoldingPage';
import { ProfilesPage } from '@/features/imports/ProfilesPage';
import { MeetingsPage } from '@/features/meetings/MeetingsPage';
import { SessionPage } from '@/features/meetings/SessionPage';
import { AiHistoryPage } from '@/features/payees/AiHistoryPage';
import { PayeePage } from '@/features/payees/PayeePage';
import { PayeesPage } from '@/features/payees/PayeesPage';
import { CashFlowPage } from '@/features/planned/CashFlowPage';
import { LegacyPlannedRedirect } from '@/features/planned/LegacyPlannedRedirect';
import { PlannedPage } from '@/features/planned/PlannedPage';
import { EnvelopePage } from '@/features/envelope/EnvelopePage';
import { AuthError } from '@/routes/AuthError';
import { AuthReturn } from '@/routes/AuthReturn';
import { AuditPage } from '@/features/audit/AuditPage';
import { AssistantLinkRedirect } from '@/features/assistant/AssistantLinkRedirect';
import { AssistantPage } from '@/features/assistant/AssistantPage';
import { PlaidReturnPage } from '@/features/connections/PlaidReturnPage';
import { NotFound } from '@/routes/NotFound';
import { RootLayout } from '@/routes/RootLayout';
import { RouteError } from '@/routes/RouteError';
import { SessionExpired } from '@/routes/SessionExpired';
import { SignedOut } from '@/routes/SignedOut';
import { EntryGate } from '@/session/EntryGate';
import { MeGate } from '@/session/MeGate';
import { AppShell } from '@/shell/AppShell';
import { CommunityPage } from '@/features/community/CommunityPage';
import { HomePage } from '@/features/home/HomePage';
import { ToolsOverviewPage } from '@/features/tools/ToolsOverviewPage';

// Routes mirror the API's scoping (design §14.3): owner-scoped surfaces under `/me/…`, a
// virtual account and everything in it under `/v/{virtualAccountId}/…`.
export const routes: RouteObject[] = [
  {
    element: <RootLayout />,
    errorElement: <RouteError />,
    children: [
      { path: '/auth/return', element: <AuthReturn /> },
      { path: '/auth/error', element: <AuthError /> },
      { path: '/auth/signed-out', element: <SignedOut /> },
      { path: '/auth/session-expired', element: <SessionExpired /> },
      { path: '/ref/:code', element: <ReferralLanding /> },
      {
        element: <AuthGate />,
        children: [
          {
            element: <MeGate />,
            children: [
              { path: '/shared/:virtualAccountId', element: <SharedLanding /> },
              // The retired journey's addresses (TMMAPP-0116), ahead of the entry gate: they land on
              // My Accounts, where the gate shows a NONE person the subscribe dialog.
              { path: '/get-started', element: <Navigate to="/me/accounts" replace /> },
              { path: '/get-started/*', element: <Navigate to="/me/accounts" replace /> },
              { path: '/welcome', element: <Navigate to="/me/accounts" replace /> },
              { path: '/welcome/*', element: <Navigate to="/me/accounts" replace /> },
              { path: '/subscribe', element: <Navigate to="/me/accounts" replace /> },
              {
                element: <EntryGate />,
                children: [
                  { path: '/me/automation-help', element: <ConsentHelpPage /> },
                  // The retired setup wizard (TMMAPP-0141): an old link lands on Payees.
                  { path: '/me/genesis', element: <Navigate to="/me/payees" replace /> },
                  // The ticket emails link to /assistant (TMMAPP-0142 §G U2): Help & feedback.
                  { path: '/assistant', element: <AssistantLinkRedirect /> },
                  {
                    element: <AppShell />,
                    children: [
                      { path: '/', element: null },
                      // Home is the landing page (TMMAPP-0152 §A 1); Community is a designed Coming
                      // soon page (§C). Our Money Meeting is retired: its links land on My Money
                      // Meetings (TMMAPP-0159 §B 6).
                      { path: '/me/home', element: <HomePage /> },
                      { path: '/me/meeting', element: <Navigate to="/me/meetings" replace /> },
                      { path: '/me/tools', element: <ToolsOverviewPage /> },
                      { path: '/community', element: <CommunityPage /> },
                      { path: '/me/virtual-accounts', element: <VirtualAccountsPage /> },
                      { path: '/me/own-books', element: <OwnBooksPage /> },
                      { path: '/v/:virtualAccountId', element: <VirtualAccountPage /> },
                      {
                        path: '/v/:virtualAccountId/meetings/:meetingId/:occurrenceKey',
                        element: <SessionPage />,
                      },
                      { path: '/v/:virtualAccountId/e/:envelopeId', element: <EnvelopePage /> },
                      { path: '/me/accounts', element: <AccountsPage /> },
                      { path: '/me/search', element: <RegisterSearchPage /> },
                      { path: '/connect/plaid/return', element: <PlaidReturnPage /> },
                      { path: '/me/holdings/:holdingId', element: <HoldingPage /> },
                      { path: '/me/accounts/profiles', element: <ProfilesPage /> },
                      { path: '/me/accounts/:accountId/import', element: <AccountPage /> },
                      {
                        path: '/me/accounts/:accountId/import/:batchId',
                        element: <AccountPage />,
                      },
                      { path: '/me/accounts/:accountId', element: <AccountPage /> },
                      { path: '/me/payees', element: <PayeesPage /> },
                      { path: '/me/payees/:payeeId', element: <PayeePage /> },
                      { path: '/me/ai-requests', element: <AiHistoryPage /> },
                      { path: '/me/assistant', element: <AssistantPage /> },
                      { path: '/me/assistant/:sessionId', element: <AssistantPage /> },
                      { path: '/me/recurring', element: <PlannedPage /> },
                      { path: '/me/cash-flow', element: <CashFlowPage /> },
                      // The retired "Planned / Cashflow forecast" (TMMAPP-0145): an old link lands
                      // on Recurring, or on Cash flow for its cash flow view.
                      { path: '/me/planned', element: <LegacyPlannedRedirect /> },
                      { path: '/me/meetings', element: <MeetingsPage /> },
                      // A standalone meeting's session (TMMAPP-0159 §D 22): no virtual account,
                      // so it lives under My Money Meetings, as its API lives under /v1/me/meetings.
                      {
                        path: '/me/meetings/:meetingId/:occurrenceKey',
                        element: <SessionPage standalone />,
                      },
                      { path: '/me/audit', element: <AuditPage /> },
                      { path: '/me/settings', element: <SettingsPage /> },
                    ],
                  },
                  { path: '*', element: <NotFound /> },
                ],
              },
            ],
          },
        ],
      },
    ],
  },
];

export const router = createBrowserRouter(routes);
